import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-6 px-6 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Servisfy</h1>
            <p className="text-purple-200 text-sm">by ELSE DIGITAL SOLUTIONS INC.</p>
          </div>
          <Link
            to="/login"
            className="text-sm text-purple-200 hover:text-white underline underline-offset-2 transition-colors"
          >
            ← Back to Login
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 text-sm mb-10">
          Last Updated: May 9, 2026 &nbsp;|&nbsp; Effective Date: May 9, 2026
        </p>

        <div className="space-y-10 text-gray-700 leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              ELSE DIGITAL SOLUTIONS INC. ("Company," "we," "our," or "us") operates the Servisfy
              platform, including the website <strong>servisfy.com</strong>, the Servisfy CRM
              application, and associated communication services (collectively, the "Service").
            </p>
            <p className="mt-3">
              This Privacy Policy explains how we collect, use, disclose, and protect personal
              information when you use our Service, including when you communicate with us or our
              customers via SMS, WhatsApp, or voice calls using our platform.
            </p>
            <p className="mt-3">
              By using the Service, you agree to the collection and use of information in
              accordance with this policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
            <p className="mb-2">We may collect the following categories of personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Contact Information:</strong> Name, email address, phone number, and
                business name provided during account registration or customer interactions.
              </li>
              <li>
                <strong>Communication Data:</strong> Content of messages sent through our
                platform via SMS, WhatsApp, or voice, including transcriptions generated for
                support purposes.
              </li>
              <li>
                <strong>Usage Data:</strong> Log data such as IP addresses, browser type, pages
                visited, timestamps, and interaction history within the Service.
              </li>
              <li>
                <strong>Device Information:</strong> Device identifiers, operating system, and
                application version when accessing the Service via mobile or web applications.
              </li>
              <li>
                <strong>Business Data:</strong> Information related to customer support tickets,
                service requests, conversation history, and account configuration.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              3. SMS and WhatsApp Messaging — Consent and Opt-In
            </h2>
            <p className="mb-3">
              Servisfy enables businesses to communicate with their customers via SMS and
              WhatsApp messaging powered by Twilio. The following terms apply to all
              SMS and WhatsApp communications sent through our platform:
            </p>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <strong>Consent:</strong> By providing your phone number to a business using the
                Servisfy platform and opting in to receive messages, you consent to receive
                automated text messages from that business, which may include customer support
                responses, service notifications, appointment reminders, and account alerts.
              </li>
              <li>
                <strong>Message Frequency:</strong> Message frequency varies based on your
                interactions and the services subscribed to by the business you are communicating
                with. You may receive multiple messages per interaction.
              </li>
              <li>
                <strong>Message and Data Rates:</strong> Standard message and data rates may
                apply. These charges are assessed by your mobile carrier and are your
                responsibility.
              </li>
              <li>
                <strong>How to Opt Out (STOP):</strong> To stop receiving SMS messages from a
                Servisfy-powered number at any time, reply <strong>STOP</strong> to any message
                you receive. You will receive a one-time confirmation message and no further
                messages will be sent to your number from that sender.
              </li>
              <li>
                <strong>How to Get Help (HELP):</strong> To receive information about our
                messaging service, reply <strong>HELP</strong> to any message you receive. You
                may also contact us directly at{" "}
                <a
                  href="mailto:support@servisfy.com"
                  className="text-purple-700 hover:underline"
                >
                  support@servisfy.com
                </a>
                .
              </li>
              <li>
                <strong>Supported Carriers:</strong> Servisfy messaging services are available on
                all major US wireless carriers. Carrier liability is not assumed by Servisfy or
                ELSE DIGITAL SOLUTIONS INC.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              4. How We Use Your Information
            </h2>
            <p className="mb-2">We use collected information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Delivering, operating, and maintaining the Servisfy platform and its features.</li>
              <li>
                Processing customer support interactions and routing communications to the
                appropriate business or support agent.
              </li>
              <li>
                Providing AI-powered auto-resolution of customer queries using our secure
                solution-matching engine.
              </li>
              <li>
                Sending transactional and service-related messages you have consented to receive.
              </li>
              <li>Analyzing platform usage to improve our features and user experience.</li>
              <li>Detecting and preventing fraud, abuse, or unauthorized access to the Service.</li>
              <li>Complying with legal obligations and regulatory requirements.</li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              5. Sharing of Information
            </h2>
            <p className="mb-3">
              We do not sell, trade, or rent your personal information to third parties for
              marketing purposes. We may share your information only in the following
              circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Service Providers:</strong> With trusted third-party providers who assist
                in operating our platform, including cloud infrastructure (Google Cloud Platform),
                messaging infrastructure (Twilio), database services (MongoDB Atlas), and AI
                processing (OpenAI). These providers are bound by confidentiality agreements and
                are not permitted to use your data for their own purposes.
              </li>
              <li>
                <strong>Business Operators:</strong> With the business whose Servisfy account
                you are interacting with, as necessary to provide the requested support or service.
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by applicable law, legal
                process, or governmental authority, or to protect the rights and safety of our
                users and the public.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or
                sale of all or substantially all of our assets, your information may be
                transferred as part of that transaction.
              </li>
            </ul>
            <p className="mt-4 font-medium text-gray-900">
              No mobile information will be shared with third parties or affiliates for marketing
              or promotional purposes. Information sharing to subcontractors in support services,
              such as customer service, is permitted. All other use case categories exclude text
              messaging originator opt-in data and consent; this information will not be shared
              with any third parties.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention</h2>
            <p>
              We retain personal information for as long as necessary to provide the Service and
              fulfill the purposes described in this policy, unless a longer retention period is
              required or permitted by law. Conversation histories, message logs, and support
              records are retained for a period of up to 24 months unless deleted earlier upon
              request or in accordance with applicable law.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal
              information against unauthorized access, alteration, disclosure, or destruction.
              These measures include encrypted data transmission (TLS), access controls,
              authentication systems, and regular security reviews. However, no method of
              electronic transmission or storage is 100% secure, and we cannot guarantee
              absolute security.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your Rights</h2>
            <p className="mb-2">
              Depending on your jurisdiction, you may have the following rights regarding your
              personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The right to access the personal information we hold about you.</li>
              <li>The right to request correction of inaccurate or incomplete information.</li>
              <li>The right to request deletion of your personal information.</li>
              <li>The right to withdraw consent for messaging communications at any time.</li>
              <li>The right to lodge a complaint with a data protection authority.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at{" "}
              <a
                href="mailto:support@servisfy.com"
                className="text-purple-700 hover:underline"
              >
                support@servisfy.com
              </a>
              .
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              9. Children's Privacy
            </h2>
            <p>
              The Servisfy Service is not directed to individuals under the age of 18. We do not
              knowingly collect personal information from children. If you believe we have
              inadvertently collected information from a child, please contact us immediately and
              we will take steps to delete that information.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we make material
              changes, we will update the "Last Updated" date at the top of this page and, where
              appropriate, notify users via email or through the Service. Your continued use of
              the Service after such changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or
              our data practices, please contact us:
            </p>
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-1 text-sm">
              <p className="font-semibold text-gray-900">ELSE DIGITAL SOLUTIONS INC.</p>
              <p>Operating as: Servisfy</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:support@servisfy.com"
                  className="text-purple-700 hover:underline"
                >
                  support@servisfy.com
                </a>
              </p>
              <p>Website: servisfy.com</p>
            </div>
          </section>

          {/* Footer links */}
          <div className="pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between text-sm text-gray-500">
            <span>© 2026 ELSE DIGITAL SOLUTIONS INC. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/privacy-policy" className="text-purple-700 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="text-purple-700 hover:underline">
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
